<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'

const { t, tm, locale } = useI18n()
const isDark = ref(false)
const isMobileMenuOpen = ref(false)
const APP_URL = 'https://app.nomadianelite.com'

// Case Study Carousel
const caseStudies = [
  { id: 'challenge', titleKey: 'caseStudy.challenge.title', descKey: 'caseStudy.challenge.desc', image: '/images/man-facing-zamzam.png' },
  { id: 'solution', titleKey: 'caseStudy.solution.title', descKey: 'caseStudy.solution.desc', image: '/images/kaba-shake-hand.png' },
  { id: 'outcome', titleKey: 'caseStudy.outcome.title', descKey: 'caseStudy.outcome.desc', image: '/images/sunny-jeddah-image.png' }
]
const activeCaseStudyIndex = ref(0)
let caseStudyInterval: ReturnType<typeof setInterval> | null = null

const startCarousel = () => {
    if (caseStudyInterval) clearInterval(caseStudyInterval)
    caseStudyInterval = setInterval(() => {
        activeCaseStudyIndex.value = (activeCaseStudyIndex.value + 1) % caseStudies.length
    }, 5000)
}

const setActiveCaseStudy = (index: number) => {
    activeCaseStudyIndex.value = index
    startCarousel()
}

const languages = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'id', label: 'Bahasa Indonesia', dir: 'ltr' }
]

const currentLang = computed(() => {
    return languages.find(l => l.code === locale.value) || languages[0]
})

const showLanguageModal = ref(false)

// Language Management
const changeLanguage = (code: string) => {
    const lang = languages.find(l => l.code === code)
    if (lang) {
        locale.value = code
        localStorage.setItem('locale', code)
        document.documentElement.dir = lang.dir
        document.documentElement.lang = code
        showLanguageModal.value = false
        document.body.style.overflow = ''
    }
}

// Theme Management
const toggleTheme = () => {
  isDark.value = !isDark.value
  updateThemeClass()
}

const updateThemeClass = () => {
  if (isDark.value) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', 'dark')
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', 'light')
  }
}

// Mobile Menu
const toggleMobileMenu = () => {
    isMobileMenuOpen.value = !isMobileMenuOpen.value
    if (isMobileMenuOpen.value) {
        document.body.style.overflow = 'hidden'
    } else {
        document.body.style.overflow = ''
    }
}

onMounted(() => {
  // Theme init
  const savedTheme = localStorage.getItem('theme')
  if (savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
    isDark.value = true
  }
  updateThemeClass()

  // Locale init
  const savedLang = localStorage.getItem('locale')
  if (!savedLang) {
      showLanguageModal.value = true
      document.body.style.overflow = 'hidden'
  } else {
      const langConfig = languages.find(l => l.code === savedLang)
      if (langConfig) {
          document.documentElement.dir = langConfig.dir
          document.documentElement.lang = savedLang
      }
  }

  // Start Carousel
  startCarousel()
})

onUnmounted(() => {
    if (caseStudyInterval) clearInterval(caseStudyInterval)
})
</script>

<template>
  <div class="min-h-screen bg-[#FDFBF7] dark:bg-bg-primary text-bg-primary dark:text-text-primary font-sans selection:bg-orange-200 transition-colors duration-300">
    <!-- Language Selection Modal -->
    <div v-if="showLanguageModal" class="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <div class="bg-white dark:bg-bg-card max-w-md w-full rounded-2xl p-8 shadow-2xl text-center transform scale-100 transition-all">
            <h2 class="text-2xl font-serif font-bold mb-2 text-bg-primary dark:text-white">Welcome / أهلاً بك / Selamat Datang</h2>
            <p class="text-gray-500 dark:text-gray-400 mb-8">Please select your preferred language<br>الرجاء اختيار لغتك المفضلة<br>Silakan pilih bahasa pilihan Anda</p>
            
            <div class="flex flex-col gap-3">
                <button 
                    v-for="lang in languages" 
                    :key="lang.code"
                    @click="changeLanguage(lang.code)"
                    class="w-full py-4 px-6 rounded-xl border border-gray-200 dark:border-border hover:border-black dark:hover:border-white hover:bg-gray-50 dark:hover:bg-bg-elevated transition-all flex items-center justify-between group"
                >
                    <span class="font-medium text-lg">{{ lang.label }}</span>
                    <span class="text-gray-400 group-hover:text-black dark:group-hover:text-white transition-colors">→</span>
                </button>
            </div>
        </div>
    </div>
    <!-- Navigation -->
    <nav class="sticky top-0 z-50 bg-[#FDFBF7]/90 dark:bg-bg-primary/90 backdrop-blur-md border-b border-gray-200/50 dark:border-border transition-colors duration-300">
      <div class="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <!-- Logo Switching -->
          <img v-if="isDark" src="/images/logo_net_white.png" alt="Nomadian Logo" class="h-32 w-auto" />
          <img v-else src="/images/logo_net_black.png" alt="Nomadian Logo" class="h-32 w-auto" />
          <span class="font-bold text-xl tracking-tight hidden">Nomadian</span>
        </div>
        
        <!-- Desktop Nav -->
        <div class="hidden md:flex items-center gap-6">
            <div class="flex items-center space-x-8 rtl:space-x-reverse text-sm font-medium text-gray-800 dark:text-gray-200">
                <RouterLink to="/" class="hover:text-blue-600 dark:hover:text-white transition-colors">{{ t('footer.links.home') }}</RouterLink>
                <RouterLink to="/philosophy" class="hover:text-blue-600 dark:hover:text-white transition-colors">{{ t('nav.philosophy') }}</RouterLink>
                <RouterLink to="/about" class="hover:text-blue-600 dark:hover:text-white transition-colors">{{ t('nav.about') }}</RouterLink>
            </div>
            
            <div class="flex items-center gap-3 border-l pl-6 rtl:pl-0 rtl:border-l-0 rtl:border-r rtl:pr-6 border-gray-300 dark:border-gray-700">
                 <!-- Language Switcher -->
                <div class="relative group">
                    <button class="flex items-center gap-1 text-sm font-medium hover:text-blue-600 dark:hover:text-white">
                        <span>{{ currentLang.code.toUpperCase() }}</span>
                        <span class="text-[10px]">▼</span>
                    </button>
                    <div class="absolute top-full right-0 rtl:right-auto rtl:left-0 mt-2 w-32 bg-white dark:bg-bg-card rounded-lg shadow-lg border border-gray-100 dark:border-border py-1 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all transform origin-top-right rtl:origin-top-left z-50">
                        <button 
                            v-for="lang in languages" 
                            :key="lang.code"
                            @click="changeLanguage(lang.code)"
                            class="w-full text-left rtl:text-right px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-bg-elevated transition-colors"
                            :class="{'font-bold text-blue-600': locale === lang.code}"
                        >
                            {{ lang.label }}
                        </button>
                    </div>
                </div>

                 <!-- Theme Toggle -->
                <button @click="toggleTheme" class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                    <span v-if="isDark">☀️</span>
                    <span v-else>🌙</span>
                </button>
                <a
                    :href="APP_URL"
                    class="inline-flex items-center justify-center rounded-full bg-bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-black/90 dark:bg-white dark:text-bg-primary dark:hover:bg-gray-200"
                >
                    {{ t('nav.login') }}
                </a>
            </div>
        </div>

        <!-- Mobile Toggle -->
        <div class="flex items-center gap-4 md:hidden">
             <!-- Theme Toggle Mobile -->
            <button @click="toggleTheme" class="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors">
                <span v-if="isDark">☀️</span>
                <span v-else>🌙</span>
            </button>

             <!-- Hamburger -->
            <button @click="toggleMobileMenu" class="p-2 -mr-2">
                <div class="w-6 h-5 flex flex-col justify-between">
                    <span class="w-full h-0.5 bg-current transform transition-transform" :class="{'rotate-45 translate-y-2': isMobileMenuOpen}"></span>
                    <span class="w-full h-0.5 bg-current opacity-100 transition-opacity" :class="{'opacity-0': isMobileMenuOpen}"></span>
                    <span class="w-full h-0.5 bg-current transform transition-transform" :class="{'-rotate-45 -translate-y-2.5': isMobileMenuOpen}"></span>
                </div>
            </button>
        </div>
      </div>
    </nav>

    <!-- Mobile Menu Overlay -->
    <div v-if="isMobileMenuOpen" class="fixed inset-0 z-40 bg-[#FDFBF7] dark:bg-bg-primary pt-24 px-6 md:hidden">
        <div class="flex flex-col space-y-6 text-xl font-medium">
             <RouterLink to="/" @click="toggleMobileMenu" class="border-b border-gray-200 dark:border-gray-800 pb-4">{{ t('footer.links.home') }}</RouterLink>
             <RouterLink to="/philosophy" @click="toggleMobileMenu" class="border-b border-gray-200 dark:border-gray-800 pb-4">{{ t('nav.philosophy') }}</RouterLink>
             <RouterLink to="/about" @click="toggleMobileMenu" class="border-b border-gray-200 dark:border-gray-800 pb-4">{{ t('nav.about') }}</RouterLink>
             
             <!-- Mobile Config -->
             <div class="pt-4 flex flex-col gap-4">
                 <div class="flex gap-2 overflow-x-auto pb-2">
                     <button 
                        v-for="lang in languages"
                        :key="lang.code"
                        @click="changeLanguage(lang.code)"
                        class="px-4 py-2 rounded-full border text-sm whitespace-nowrap"
                        :class="locale === lang.code ? 'border-black dark:border-white bg-black dark:bg-white text-white dark:text-black' : 'border-gray-300 dark:border-gray-700'"
                     >
                        {{ lang.label }}
                     </button>
                 </div>
                 <a
                    :href="APP_URL"
                    class="block w-full rounded-xl bg-bg-primary px-5 py-3 text-center text-base font-semibold text-white transition-colors dark:bg-white dark:text-bg-primary"
                 >
                    {{ t('nav.login') }}
                 </a>
             </div>
        </div>
    </div>

    <main>
      <!-- Hero Section -->
      <section class="pt-12 md:pt-20 pb-20 md:pb-32 px-6 max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center scroll-mt-24">
        <div class="order-2 md:order-1">
          <h1 class="text-4xl md:text-5xl lg:text-7xl font-serif font-medium leading-[1.1] mb-8 text-bg-primary dark:text-white">
            {{ t('hero.title') }}
          </h1>
          
          <div class="mb-12">
             <a
                :href="APP_URL"
                class="inline-flex items-center justify-center rounded-lg bg-bg-primary px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-black/90 dark:bg-white dark:text-bg-primary dark:hover:bg-gray-200"
             >
                {{ t('nav.login') }}
             </a>
          </div>

           <div class="flex items-center space-x-8 rtl:space-x-reverse text-sm font-medium border-t border-gray-200 dark:border-border pt-8 text-bg-primary dark:text-white">
             <div>
                <div class="text-2xl font-serif mb-1">$50</div>
                <div class="text-gray-500 dark:text-gray-400">{{ t('hero.yield') }}</div>
             </div>
              <div>
                <div class="text-2xl font-serif mb-1">100%</div>
                <div class="text-gray-500 dark:text-gray-400">{{ t('hero.managed') }}</div>
             </div>
           </div>
        </div>
        
        <div class="relative order-1 md:order-2">
          <!-- Hero Image -->
          <div class="aspect-square bg-gray-100 dark:bg-bg-card rounded-2xl overflow-hidden relative border border-gray-200 dark:border-border shadow-2xl rotate-3 hover:rotate-0 transition-transform duration-500">
             <div class="absolute inset-0 bg-[url('/images/hotel.jpg')] bg-cover bg-center"></div>
          </div>
        </div>
      </section>

      <!-- Services List Section -->
      <section class="py-20 md:py-24 px-6 border-t border-gray-200 dark:border-border">
        <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
            <div>
                 <h2 class="text-3xl md:text-4xl lg:text-5xl font-serif leading-tight mb-8 text-bg-primary dark:text-white">
                    {{ t('services.title') }}
                </h2>
            </div>
            <div class="space-y-8">
                <div v-for="(item, index) in (tm('services.items') as Array<{title: string, desc: string}>)" :key="index" class="flex items-start gap-4">
                    <div class="w-6 h-6 rounded-full border border-black dark:border-white flex items-center justify-center mt-1 flex-shrink-0 text-bg-primary dark:text-white">
                        <span class="text-xs">{{ index + 1 }}</span>
                    </div>
                    <div>
                        <h3 class="font-bold mb-2 text-bg-primary dark:text-white">{{ item.title }}</h3>
                        <p class="text-gray-600 dark:text-gray-400 leading-relaxed">{{ item.desc }}</p>
                    </div>
                </div>
            </div>
        </div>
      </section>

      <!-- Black Banner Section -->
      <section class="bg-bg-primary text-white py-20 px-6 dark:bg-bg-card dark:border-y dark:border-border">
         <div class="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12">
            <div>
                <h2 class="text-3xl font-serif mb-4 whitespace-pre-line">{{ t('awards.title') }}</h2>
                <p class="text-gray-400">{{ t('awards.subtitle') }}</p>
                <RouterLink to="/about" class="mt-8 inline-block bg-white text-bg-primary px-6 py-3 rounded-full font-medium hover:bg-gray-100 transition-colors">
                    {{ t('awards.cta') }}
                </RouterLink>
            </div>
            
            <div class="mt-5 ">
                <img src="/images/saudi-vision.png" alt="Saudi Vision 2030" class="h-48 w-auto" />
            </div>
         </div>
      </section>

      <!-- Fee Comparison Section -->
      <section class="py-20 md:py-24 px-6 bg-[#F5F2EB] dark:bg-bg-secondary transition-colors duration-300">
         <div class="max-w-7xl mx-auto">
            <h2 class="text-3xl md:text-4xl font-serif mb-16 max-w-xl text-bg-primary dark:text-white">{{ t('fees.title') }}</h2>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
                <!-- Nomadian Card -->
                <div class="bg-bg-primary dark:bg-bg-card text-white p-8 md:p-10 rounded-2xl relative overflow-hidden border border-transparent dark:border-border shadow-xl">
                    <div class="relative z-10">
                        <div class="text-lg font-bold tracking-wide uppercase text-gray-400 mb-2">{{ t('fees.nomadian.title') }}</div>
                        <div class="text-5xl font-serif text-white mb-8">10% APY</div>
                        <ul class="space-y-4 text-gray-300 mb-10">
                            <li v-for="feat in (tm('fees.nomadian.features') as string[])" :key="feat" class="flex items-center gap-3">✓ {{ feat }}</li>
                        </ul>
                        <a :href="APP_URL" class="block w-full bg-white text-bg-primary py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors text-center">{{ t('fees.nomadian.cta') }}</a>
                    </div>
                </div>

                <!-- Traditional Card -->
                <div class="bg-white dark:bg-bg-primary p-8 md:p-10 rounded-2xl border border-gray-200 dark:border-border shadow-sm">
                     <div class="text-sm font-medium tracking-wide uppercase text-gray-400 mb-2">{{ t('fees.traditional.title') }}</div>
                        <div class="text-5xl font-serif mb-8 text-gray-300 dark:text-gray-600">High</div>
                        <div class="text-2xl font-serif mb-6 line-through text-gray-300 dark:text-gray-600">$100,000+</div>
                        <ul class="space-y-4 text-gray-500 dark:text-gray-400 mb-10">
                             <li v-for="feat in (tm('fees.traditional.features') as string[])" :key="feat" class="flex items-center gap-3">{{ feat }}</li>
                        </ul>

                </div>
            </div>
         </div>
      </section>

      <!-- RSU Case Study Section -->
      <section class="py-20 md:py-32 px-6 border-t border-gray-200 dark:border-border dark:bg-bg-primary overflow-hidden relative">
        <div class="max-w-7xl mx-auto mb-16">
             <div class="flex flex-col md:flex-row items-start md:items-end justify-between gap-4">
                <h2 class="text-3xl md:text-5xl font-serif max-w-2xl text-bg-primary dark:text-white leading-tight">{{ t('caseStudy.title') }}</h2>
                <div class="text-xl md:text-2xl font-serif text-gray-400 dark:text-gray-500">{{ t('caseStudy.label') }}</div>
             </div>
        </div>
        
        <div class="max-w-7xl mx-auto">
            <div class="flex flex-col md:flex-row bg-white dark:bg-bg-card md:rounded-[2rem] shadow-2xl overflow-hidden min-h-[500px]">
                
                <!-- Left: Image -->
                <!-- md:w-[55%] covers a bit more than half, letting the sloped edge shape the remaining right panel -->
                <div class="w-full md:w-[60%] relative h-[350px] md:h-auto overflow-hidden">
                    <TransitionGroup name="fade">
                        <template v-for="(study, index) in caseStudies" :key="study.id">
                            <div v-show="activeCaseStudyIndex === index" 
                                 class="absolute inset-0 bg-cover bg-center transition-transform duration-1000 transform"
                                 :class="activeCaseStudyIndex === index ? 'scale-100' : 'scale-110'"
                                 :style="{ backgroundImage: `url(${study.image})` }">
                            </div>
                        </template>
                    </TransitionGroup>
                </div>

                <!-- Right: Carousel Content -->
                <!-- md:-ml-[15%] pulls the right panel leftwards to overlap the image. 
                     clip-path cuts the left edge of the right panel at a 60-degree-like slope (polygon).
                -->
                <div class="w-full md:w-[55%] relative flex flex-col justify-center bg-white dark:bg-bg-card p-8 md:p-16 lg:p-24 md:-ml-[15%] text-bg-primary dark:text-white"
                     style="clip-path: polygon(0 100%, 15% 0, 100% 0, 100% 100%);">
                    
                    <div class="relative z-10 w-full min-h-[16rem] md:pl-8">
                        <TransitionGroup name="slide-up">
                            <template v-for="(study, index) in caseStudies" :key="study.id">
                                <div v-show="activeCaseStudyIndex === index" class="absolute inset-x-0 inset-y-0 flex flex-col justify-center">
                                    <h3 class="text-3xl md:text-4xl font-bold mb-6 text-bg-primary dark:text-white">{{ t(study.titleKey) }}</h3>
                                    <p class="text-lg md:text-xl text-gray-600 dark:text-gray-400 leading-relaxed font-light">{{ t(study.descKey) }}</p>
                                </div>
                            </template>
                        </TransitionGroup>
                    </div>

                    <!-- Carousel Controls -->
                    <div class="flex items-center gap-3 mt-12 relative z-10 md:pl-8">
                        <button v-for="(_, index) in caseStudies" :key="index"
                                @click="setActiveCaseStudy(index)"
                                class="h-2 rounded-full transition-all duration-500 ease-out"
                                :class="activeCaseStudyIndex === index ? 'w-16 bg-black dark:bg-white' : 'w-4 bg-gray-200 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-500'"
                                :aria-label="`Go to slide ${index + 1}`"
                        ></button>
                    </div>
                </div>

            </div>
        </div>
      </section>

    </main>

    <!-- Footer -->
    <footer class="bg-bg-primary text-white py-20 px-6 dark:bg-black dark:border-t dark:border-border">
      <div class="max-w-7xl mx-auto">
        <div class="flex flex-col md:flex-row justify-between items-start mb-16 gap-12">
             <div class="font-bold text-2xl">Nomadian Elite</div>
             <div class="grid grid-cols-2 gap-16">
                 <div>
                     <h4 class="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">{{ t('footer.navTitle') }}</h4>
                     <div class="flex flex-col gap-3 text-sm text-gray-400">
                         <RouterLink to="/" class="hover:text-white transition-colors">{{ t('footer.links.home') }}</RouterLink>
                         <RouterLink to="/philosophy" class="hover:text-white transition-colors">{{ t('nav.philosophy') }}</RouterLink>
                         <RouterLink to="/about" class="hover:text-white transition-colors">{{ t('footer.links.about') }}</RouterLink>
                     </div>
                 </div>
                 <div>
                     <h4 class="text-sm font-semibold uppercase tracking-wider text-gray-300 mb-4">{{ t('footer.legalTitle') }}</h4>
                     <div class="flex flex-col gap-3 text-sm text-gray-400">
                         <a href="#" class="hover:text-white transition-colors">{{ t('footer.links.privacy') }}</a>
                         <a href="#" class="hover:text-white transition-colors">{{ t('footer.links.terms') }}</a>
                     </div>
                 </div>
             </div>
        </div>
        
        <div class="border-t border-gray-800 pt-12 text-xs text-gray-500 leading-relaxed text-justify">
            <p class="mb-4">
               {{ t('footer.disclaimer1') }}
            </p>
            <p>
               {{ t('footer.disclaimer2') }}
            </p>
             <div class="mt-8 flex justify-between items-center">
                <span>{{ t('footer.copyright') }}</span>
                <div class="flex gap-4">
                    <span>Twitter</span>
                    <span>LinkedIn</span>
                </div>
             </div>
        </div>
      </div>
    </footer>
  </div>

</template>

<style scoped>
/* Responsive angle for the right panel */
@media (max-width: 767px) {
  /* Reset clip path for mobile so the layout stacks naturally */
  [style*="clip-path"] {
    clip-path: none !important;
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-up-enter-from {
  opacity: 0;
  transform: translateY(30px);
}
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(-30px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s cubic-bezier(0.16, 1, 0.3, 1);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
